import React, { useCallback, useContext, useEffect, useMemo } from "react";

import {
  Linking,
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
} from "react-native";

import Avatar from "~/components/Avatar";
import Row from "~/layouts/Row";
import Col from "~/layouts/Col";
import Heading from "~/components/Heading";
import palette from "~/styles/palette";
import ParticipantActionBar from "~/components/Participant/ParticipantActionBar/index.web";
import ParticipantContext from "~/contexts/ParticipantContext";
import TeamContext from "~/contexts/TeamContext";
import useTwilio from "~/hooks/useTwilio";
import { safeParseNumber } from "~/helpers/phoneNumbers";

function toTitleCase(str) {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

export default function ParticipantHeader() {
  const { customer, participant } = useContext(ParticipantContext);

  const { members, getMembers } = useContext(TeamContext);

  const { startCall, activeCall } = useTwilio();
  const { name, email, first_name, last_name, country_code, phone_number } =
    participant || {};

  useEffect(() => {
    getMembers();
  }, []);

  const assignedTo = useMemo(() => {
    if (customer?.assigned_to && members.length > 0) {
      const teamMember = members.find((u) => u.id === customer.assigned_to);

      if (teamMember) {
        return `${teamMember.first_name} ${teamMember.last_name}`;
      }
    } else if (!customer?.assigned_to) {
      return "Unassigned";
    }

    return null;
  }, [members, customer]);

  const address = useMemo(() => {
    let addressComponents = [];

    if (customer?.address_1) {
      addressComponents.push(`${customer.address_1},`);
    }

    if (customer?.address_2) {
      addressComponents.push(`${customer.address_2},`);
    }

    if (customer?.city) {
      addressComponents.push(customer.city);
    }

    if (customer?.state) {
      addressComponents.push(customer.state);
    }

    if (customer?.zip_code) {
      addressComponents.push(customer.zip_code);
    }

    if (addressComponents.length < 1) {
      return null;
    }

    return addressComponents.join(" ");
  }, [customer]);

  const serviceAddress = useMemo(() => {
    let addressComponents = [];

    if (customer?.service_address_1) {
      addressComponents.push(`${customer.service_address_1},`);
    }

    if (customer?.service_address_2) {
      addressComponents.push(`${customer.service_address_2},`);
    }

    if (customer?.service_city) {
      addressComponents.push(customer.service_city);
    }

    if (customer?.service_state) {
      addressComponents.push(customer.service_state);
    }

    if (customer?.service_zip_code) {
      addressComponents.push(customer.service_zip_code);
    }

    if (addressComponents.length < 1) {
      return null;
    }

    return addressComponents.join(" ");
  }, [customer]);

  const billingAddress = useMemo(() => {
    let addressComponents = [];

    if (customer?.billing_address_1) {
      addressComponents.push(`${customer.billing_address_1},`);
    }

    if (customer?.billing_address_2) {
      addressComponents.push(`${customer.billing_address_2},`);
    }

    if (customer?.billing_city) {
      addressComponents.push(customer.billing_city);
    }

    if (customer?.billing_state) {
      addressComponents.push(customer.billing_state);
    }

    if (customer?.billing_zip_code) {
      addressComponents.push(customer.billing_zip_code);
    }

    if (addressComponents.length < 1) {
      return null;
    }

    return addressComponents.join(" ");
  }, [customer]);

  const hasSecondaryContact = useMemo(() => {
    return !!(
      customer?.secondary_email ||
      customer?.secondary_first_name ||
      customer?.secondary_last_name ||
      customer?.secondary_phone
    );
  }, [customer]);

  const phoneLabel = useMemo(() => {
    return country_code && phone_number
      ? safeParseNumber(phone_number, country_code).formatNational()
      : phone_number;
  }, [phone_number, country_code]);

  const onEmailPress = useCallback(
    (email) => () => {
      Linking.openURL(`mailto:${email}`);
    },
    []
  );

  const onPhonePress = useCallback(
    (phone) => () => {
      if (!activeCall) {
        startCall(phone, participant);
      }
    },
    [startCall, activeCall, participant]
  );

  return (
    <View style={[styles.participant_details__header_container]}>
      <Row pt={16}>
        <Col center>
          <Heading size={4} center>
            {name}
          </Heading>

          {name !== phoneLabel && (
            <Row center>
              <Text style={[styles.participant_details__header_number]}>
                {phoneLabel}
              </Text>
            </Row>
          )}
        </Col>
      </Row>

      <View style={[styles.participant_action_bar__container]}>
        <ParticipantActionBar />
      </View>

      {assignedTo && <DetailRow label="Assigned To" value={assignedTo} />}

      {!!customer?.status && (
        <DetailRow label="Status" value={toTitleCase(customer.status)} />
      )}

      {hasSecondaryContact && (
        <Row pl={48} pr={48} pt={16} center>
          <View style={[{ width: "100%" }]}>
            <Text
              style={[
                styles.participant_details__header_details_secondary_label,
              ]}
            >
              Primary Contact
            </Text>
          </View>
        </Row>
      )}

      {!!customer?.email && (
        <DetailRow
          label="Email"
          value={customer.email}
          link
          onPress={onEmailPress(customer.email)}
        />
      )}
      {!!customer?.alt_email && (
        <DetailRow
          label="Email 2"
          value={customer.alt_email}
          link
          onPress={onEmailPress(customer.alt_email)}
        />
      )}
      {!!customer?.alt_phone && (
        <DetailRow
          label="Phone 2"
          value={customer.alt_phone}
          link
          onPress={onPhonePress(customer.alt_phone)}
        />
      )}

      {hasSecondaryContact && (
        <Row pl={48} pr={48} pt={16} center>
          <View style={[{ width: "100%" }]}>
            <Text
              style={[
                styles.participant_details__header_details_secondary_label,
              ]}
            >
              Secondary Contact
            </Text>
          </View>
        </Row>
      )}

      {!!(customer?.secondary_first_name || customer?.secondary_last_name) && (
        <DetailRow
          label="Name"
          value={`${customer.secondary_first_name || ""} ${
            customer.secondary_last_name || ""
          }`}
        />
      )}

      {!!customer?.secondary_email && (
        <DetailRow
          label="Email"
          value={customer.secondary_email}
          link
          onPress={onEmailPress(customer.secondary_email)}
        />
      )}

      {!!customer?.secondary_phone && (
        <DetailRow
          label="Phone"
          value={customer.secondary_phone}
          link
          onPress={onPhonePress(customer.secondary_phone)}
        />
      )}

      {address && <DetailRow label="Address" value={address} fullWidthValue />}

      {serviceAddress && (
        <DetailRow
          label="Service Address"
          value={serviceAddress}
          fullWidthValue
        />
      )}

      {billingAddress && (
        <DetailRow
          label="Billing Address"
          value={billingAddress}
          fullWidthValue
        />
      )}

      {!!customer?.lead_source && (
        <DetailRow
          label="Lead Source"
          value={toTitleCase(customer?.lead_source)}
        />
      )}
    </View>
  );
}

function DetailRow({
  label,
  value,
  fullWidthValue = false,
  values = null,
  link = false,
  onPress = null,
}) {
  return (
    <Row pl={48} pr={48} pt={16} center>
      <View
        style={[
          styles.participant_details__header_details__label_col,
          fullWidthValue ? { width: "100%", paddingVertical: 16 } : {},
        ]}
      >
        <Text style={[styles.participant_details__header_details_label]}>
          {label}:
        </Text>
      </View>

      <View
        style={[
          styles.participant_details__header_details__col,
          fullWidthValue ? { width: "100%" } : {},
        ]}
      >
        <TouchableOpacity onPress={onPress} activeOpacity={onPress ? 0.5 : 1}>
          {values &&
            values.map((v) => (
              <Text style={[styles.participant_details__header_details_value]}>
                {v}
              </Text>
            ))}

          {!values && (
            <Text
              style={[
                styles.participant_details__header_details_value,
                link
                  ? styles.participant_details__header_details_value_link
                  : {},
              ]}
            >
              {value}
            </Text>
          )}
        </TouchableOpacity>
      </View>
    </Row>
  );
}

const styles = StyleSheet.create({
  participant_details__header_container: {
    borderRadius: 16,
    backgroundColor: palette.white,
    paddingVertical: 16,
    paddingHorizontal: 16,
    marginBottom: 12,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 0,
    },
    shadowOpacity: 0.05,
    shadowRadius: 9.62,
    elevation: 4,
  },

  participant_details__header_number: {
    fontWeight: "500",
    fontSize: 18,
  },
  participant_details__header_details_label: {
    fontWeight: "700",
    fontSize: 16,
    textAlign: "left",
  },
  participant_details__header_details_secondary_label: {
    paddingTop: 16,
    fontWeight: "700",
    fontSize: 16,
    textAlign: "left",
    color: palette.info,
  },
  participant_details__header_details_value: {
    fontWeight: "400",
    fontSize: 16,
    textAlign: "left",
  },

  participant_details__header_details_value_link: {
    color: "blue",
    textDecorationLine: "underline",
    textDecorationColor: "blue",
  },

  participant_details__header_details__label_col: {
    width: "40%",
  },
  participant_details__header_details__col: {
    width: "60%",
  },
  participant_details__header_email: {
    fontWeight: "500",
    color: palette.grey,
  },
  participant_details__header_number: {},
  participant_action_bar__container: {
    marginVertical: 16,
  },
});
